// src/pages/ToDoPage.js

import React, { useEffect, useState } from 'react'
import {
  Container,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Divider,
  Box
} from '@mui/material'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import { db } from '../firebase'
import {
  collection,
  query,
  onSnapshot,
  where,
  orderBy
} from 'firebase/firestore'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { styled } from '@mui/material/styles'

import PieChartIcon from '@mui/icons-material/PieChart'
import DvrIcon from '@mui/icons-material/Dvr'
import CommentIcon from '@mui/icons-material/Comment'
import GroupsIcon from '@mui/icons-material/Groups'
import CodeIcon from '@mui/icons-material/Code'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { useTranslation } from 'react-i18next' // Çoklu dil desteği

// --- DAHA COMPACT STIL İÇİN KARTLARIMIZI STYLING ---
const CompactCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2]
}))

// Hover efektli detay kart (biraz daha compact hale getirelim)
const DetailCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: '#f3e9e7',
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: '#bbdefb'
  }
}))

export default function ToDoPage() {
  const { t } = useTranslation()
  const { currentUser, userStatus, userRole } = useAuth()
  const navigate = useNavigate()

  const [channels, setChannels] = useState([])
  const [questionForms, setQuestionForms] = useState([])
  const [userResponses, setUserResponses] = useState([])
  const [workspaces, setWorkspaces] = useState([])

  const [loading, setLoading] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  const canSeeAdminReports = userRole === 'admin' || userRole === 'moderator'
  const canSeeCardReports = canSeeAdminReports || userRole === 'observer'

  // 1) Workspace verilerini çek
  useEffect(() => {
    const wsRef = collection(db, 'workspaces')
    const unsubWs = onSnapshot(wsRef, (snapshot) => {
      const wsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))
      setWorkspaces(wsData)
    })
    return () => unsubWs()
  }, [])

  // 2) Tüm kanalları getir
  useEffect(() => {
    if (!currentUser) return

    const channelsRef = collection(db, 'channels')
    const qCh = query(channelsRef)

    const unsubscribeChannels = onSnapshot(
      qCh,
      (snapshot) => {
        const allChannels = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        setChannels(allChannels)
        setLoading(false)
      },
      (error) => {
        console.error('Kanallar alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('todoPage.channelsError'),
          severity: 'error'
        })
        setLoading(false)
      }
    )
    return () => {
      unsubscribeChannels()
    }
  }, [currentUser, t])

  // 3) Her kanal için questionForms'u dinle
  useEffect(() => {
    if (!currentUser) return
    if (channels.length === 0) {
      setQuestionForms([])
      return
    }

    let allForms = []
    const unsubscribes = []

    channels.forEach((channel) => {
      const formsRef = collection(db, 'questionForms')
      const qForms = query(
        formsRef,
        where('channelId', '==', channel.id),
        orderBy('createdAt', 'asc')
      )

      const unsubscribeForms = onSnapshot(
        qForms,
        (snapshot) => {
          const channelForms = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          }))

          // Mevcut kanala ait formları temizleyip yenilerini ekle
          allForms = allForms.filter((f) => f.channelId !== channel.id)
          allForms = [...allForms, ...channelForms]
          setQuestionForms(allForms)
        },
        (error) => {
          console.error('questionForms okunurken hata:', error)
          setSnackbar({
            open: true,
            message: t('todoPage.formsError'),
            severity: 'error'
          })
        }
      )

      unsubscribes.push(unsubscribeForms)
    })

    return () => {
      unsubscribes.forEach((unsub) => unsub())
    }
  }, [channels, currentUser, t])

  // 4) Kullanıcının verdiği yanıtları (responses) dinle
  useEffect(() => {
    if (!currentUser) return

    const responsesRef = collection(db, 'responses')
    const qResp = query(responsesRef, where('userId', '==', currentUser.uid))

    const unsubscribeResponses = onSnapshot(
      qResp,
      (snapshot) => {
        const userAnswers = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        setUserResponses(userAnswers)
      },
      (error) => {
        console.error('Kullanıcı yanıtları okunurken hata:', error)
        setSnackbar({
          open: true,
          message: t('todoPage.responsesError'),
          severity: 'error'
        })
      }
    )

    return () => {
      unsubscribeResponses()
    }
  }, [currentUser, t])

  // 5) Kanalların published formları için kullanıcı yanıtlarını duruma göre eşle
  const formsMapByChannel = {}
  channels.forEach((channel) => {
    const formsOfChannel = questionForms.filter(
      (f) => f.channelId === channel.id && f.status === 'published'
    )

    const formsEnriched = formsOfChannel.map((form) => {
      const userResp = userResponses.find(
        (r) => r.questionFormId === form.id && r.channelId === channel.id
      )
      if (!userResp) {
        return {
          ...form,
          responseStatus: 'none',
          progress: 0,
          totalQuestions: form.questionCount || 0
        }
      } else {
        return {
          ...form,
          responseStatus: userResp.status, // 'inProgress' veya 'completed'
          progress: userResp.progress || 0,
          totalQuestions: userResp.totalQuestions || form.questionCount || 0,
          rejectionMessage: userResp.rejectionMessage || ''
        }
      }
    })

    formsMapByChannel[channel.id] = formsEnriched
  })

  // Kullanıcı pasifse görev yok
  if (userStatus === 'passive') {
    return (
      <Container sx={{ mt: 6 }}>
        <Typography variant="h6">{t('todoPage.noTasks')}</Typography>
      </Container>
    )
  }

  if (loading) {
    return (
      <Container sx={{ mt: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('todoPage.loading')}
        </Typography>
      </Container>
    )
  }

  // --- Workspace'lerin üzerinden dönüyoruz ---
  // channels'i workspaceId bazında grupladık
  const channelsByWorkspace = channels.reduce((acc, channel) => {
    const wsId = channel.workspaceId || 'noWorkspaceId'
    if (!acc[wsId]) {
      acc[wsId] = []
    }
    acc[wsId].push(channel)
    return acc
  }, {})

  return (
    <Container
      sx={{
        mt: 4,
        backgroundColor: '#f9f9f9',
        padding: 3,
        borderRadius: 2
      }}
    >
      {/* Sayfa başlığı */}
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#bf9987' }}>
        <AssignmentIndIcon sx={{ mr: 1 }} />
        {t('todoPage.title')}
      </Typography>

      {/*
        Sadece userStatus 'admin' veya 'moderator' ise
        üstteki özel kartları göster
      */}
      {canSeeAdminReports && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <DetailCard onClick={() => navigate('/moderator/questions')}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <CodeIcon sx={{ mr: 1 }} />
                  {t('todoPage.createForm')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
                  {t('todoPage.createFormDesc')}
                </Typography>
              </CardContent>
            </DetailCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailCard onClick={() => navigate('/view-answers')}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <PieChartIcon sx={{ mr: 1 }} />
                  {t('todoPage.reportDashboard')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
                  {t('todoPage.reportDashboardDesc')}
                </Typography>
              </CardContent>
            </DetailCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailCard onClick={() => navigate('/view-answers-card')}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <DvrIcon sx={{ mr: 1 }} />
                  {t('todoPage.reportCard')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
                  {t('todoPage.reportCardDesc')}
                </Typography>
              </CardContent>
            </DetailCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailCard onClick={() => navigate('/aireports')}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <CommentIcon sx={{ mr: 1 }} />
                  {t('todoPage.AIreportCard')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
                  {t('todoPage.AIreportCardDesc')}
                </Typography>
              </CardContent>
            </DetailCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailCard onClick={() => navigate('/CardAnalyticsPage')}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <CommentIcon sx={{ mr: 1 }} />
                  AnalyticsPage
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
                 AnalyticsPage
                </Typography>
              </CardContent>
            </DetailCard>
          </Grid>
        </Grid>
      )}

      {!canSeeAdminReports && canSeeCardReports && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <DetailCard onClick={() => navigate('/view-answers-card')}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <DvrIcon sx={{ mr: 1 }} />
                  {t('todoPage.reportCard')}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.6rem' }}>
                  {t('todoPage.reportCardDesc')}
                </Typography>
              </CardContent>
            </DetailCard>
          </Grid>
        </Grid>
      )}

      {/* Eğer hiç kanal yoksa */}
      {channels.length === 0 ? (
        <Typography variant="body1">
          {t('todoPage.noChannelAssignment')}
        </Typography>
      ) : (
        // --- Workspace'lerin üzerinden dönüyoruz ---
        Object.keys(channelsByWorkspace).map((wsId) => {
          // İlgili workspace'i bulalım
          const workspace = workspaces.find((ws) => ws.id === wsId)
          const workspaceName = workspace ? workspace.name : 'No Workspace Name'

          // ---------------------------------------
          // YENİ EKLENEN KISIM: Kullanıcı o workspace'e ait hangi kanallara üye?
          // Eğer hiç üye kanal yoksa workspace'i göstermeyelim.
          // ---------------------------------------
          const channelsInThisWorkspace = channelsByWorkspace[wsId].filter((channel) =>
            channel?.memberDetails?.some((m) => m.userId === currentUser.uid)
          )

          // Hiç kanal kalmadıysa, bu workspace'i atla
          if (channelsInThisWorkspace.length === 0) {
            return null
          }

          return (
            <CompactCard key={wsId} sx={{ mb: 4 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {/* Workspace adını göster */}
                    {workspaceName}
                  </Typography>
                }
              />
              <Divider />

              <CardContent sx={{ paddingX: 2, paddingY: 1 }}>
                {/* Şimdi workspace içindeki her kanalı listele
                    (ancak user'ın dahil olduğu kanalları zaten yukarıda filtreledik) 
                */}
                {channelsInThisWorkspace.map((channel) => {
                  // Kullanıcının bu kanaldaki rolünü bul
                  const channelRole =
                    channel?.memberDetails?.find((m) => m.userId === currentUser.uid)?.role ||
                    'participant'

                  // Hem participant hem de moderator formları görebilsin
                  const canViewForms =
                    channelRole === 'participant' || channelRole === 'moderator'

                  // Kanaldaki form bilgilerini al
                  const formsData = formsMapByChannel[channel.id] || []
                  
                  // Sadece tamamlanmamış formları listelemek istiyorsanız:
                  const visibleForms = formsData.filter((f) => f.responseStatus !== 'completed')

                  return (
                    <Box key={channel.id} sx={{ mb: 3 }}>
                      {/* Kanal ismi */}
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {channel.name}
                      </Typography>

                      {/* SADECE PARTICIPANT İSE KANALIN WORKSPACE SAYFASINA GİRİŞ LİNKİ */}
                      {(channelRole === 'participant' ||
                        channelRole === 'moderator' ||
                        channelRole === 'observer') &&
                        channel.showChannelLink && (
                        <DetailCard
                          onClick={() =>
                            navigate(`/workspace/${channel.workspaceId}`, {
                              state: { channelId: channel.id }
                            })
                          }
                          sx={{ mb: 2 }}
                        >
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              <GroupsIcon sx={{ mr: 1 }} />
                              {t('todoPage.groupChat')}
                            </Typography>
                            <Typography variant="body2">
                              {t('todoPage.groupChatDesc')}
                            </Typography>
                          </CardContent>
                        </DetailCard>
                      )}

                      {/* Form Listesi => participant VEYA moderator görebilsin */}
                      {canViewForms ? (
                        visibleForms.length === 0 ? (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {t('todoPage.noAvailableForm')}
                          </Typography>
                        ) : (
                          <Grid container spacing={1} sx={{ mt: 1 }}>
                            {visibleForms.map((form) => {
                              const isNotStarted = form.responseStatus === 'none'
                              const isInProgress = form.responseStatus === 'inProgress'
                              const progress = form.progress || 0
                              const total = form.totalQuestions || 0
                              const completion =
                                total > 0
                                  ? Math.round((progress / total) * 100)
                                  : 0

                              return (
                                <Grid item xs={12} key={form.id}>
                                  <Card
                                    sx={{
                                      bgcolor: '#f9f9f9',
                                      // Moderator için tıklanmayı kaldıralım
                                      cursor:
                                        channelRole === 'participant'
                                          ? 'pointer'
                                          : 'default',
                                      '&:hover':
                                        channelRole === 'participant'
                                          ? { backgroundColor: '#f0f0f0' }
                                          : undefined
                                    }}
                                    // onClick'i sadece participant ise çalıştırıyoruz
                                    onClick={
                                      channelRole === 'participant'
                                        ? () => {
                                            navigate('/user/answers', {
                                              state: {
                                                channelId: channel.id,
                                                questionFormId: form.id
                                              }
                                            })
                                          }
                                        : undefined // moderator tıklayınca hiçbir şey olmayacak
                                    }
                                  >
                                    <CardHeader
                                      title={
                                        <Typography variant="subtitle2" fontWeight="bold">
                                          <NavigateNextIcon sx={{ mr: 1 }} />
                                          {form.name}
                                        </Typography>
                                      }
                                      subheader={
                                        <>
                                          {isNotStarted ? (
                                            t('todoPage.notStartedDesc')
                                          ) : isInProgress ? (
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                              }}
                                            >
                                              <CircularProgress
                                                variant="determinate"
                                                value={completion}
                                                size={20}
                                              />
                                              <Typography variant="body2">
                                                {t('todoPage.inProgressProgress', { completion })}
                                              </Typography>
                                              {form.rejectionMessage && (
                                                <Typography variant="body2" color="error">
                                                  {t('todoPage.rejectionMessage')}: {form.rejectionMessage}
                                                </Typography>
                                              )}
                                            </Box>
                                          ) : null}
                                        </>
                                      }
                                    />
                                  </Card>
                                </Grid>
                              )
                            })}
                          </Grid>
                        )
                      ) : null}
                    </Box>
                  )
                })}
              </CardContent>
            </CompactCard>
          )
        })
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
